import { SiteHeader } from "./SiteHeader";
import { SiteFooter } from "./SiteFooter";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useCartSync } from "@/hooks/useCartSync";
import { useRestoreLocaleOnce, useLocaleEffect, useMarket, useLocale } from "@/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { startWebVitals } from "@/lib/web-vitals";
import { startEngagementTracking } from "@/lib/engagement-tracking";

// Cookie banner is client-only, below-fold, and shown at most once per
// visitor. Defer its JS (and the localStorage/i18n plumbing it pulls in)
// so it doesn't block hydration.
const CookieConsent = lazy(() =>
  import("./CookieConsent").then((m) => ({ default: m.CookieConsent })),
);

export function Layout({ children }: { children: React.ReactNode }) {
  useCartSync();
  useRestoreLocaleOnce();
  useLocaleEffect();
  // Core Web Vitals -mittarointi (LCP/CLS/INP/FCP/TTFB → GA4).
  useEffect(() => {
    startWebVitals();
    startEngagementTracking();
  }, []);
  const [showConsent, setShowConsent] = useState(false);
  useEffect(() => {
    // Wait until the browser is idle so we never compete with the LCP paint.
    const cb = () => setShowConsent(true);
    const w = window as Window & { requestIdleCallback?: (cb: () => void) => number };
    if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(cb);
    else setTimeout(cb, 1200);
  }, []);
  // When market or language changes we need Shopify data (prices/currency,
  // translated titles) to refetch under the new @inContext, so bust caches.
  const qc = useQueryClient();
  const market = useMarket();
  const locale = useLocale();
  // Skip the initial mount — the loader already primed the cache under the
  // current market/locale. Invalidating on hydration forces every Shopify
  // query to refetch and throws away SSR data (major LCP/CLS regression on
  // mobile). Only invalidate when the user actually switches market/lang.
  const initial = useRef(true);
  useEffect(() => {
    if (initial.current) {
      initial.current = false;
      return;
    }
    qc.invalidateQueries();
  }, [qc, market, locale]);
  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
      {showConsent && (
        <Suspense fallback={null}>
          <CookieConsent />
        </Suspense>
      )}
    </div>
  );
}
